import { Injectable } from "@angular/core";
import { Params } from "@angular/router";
import { RouterQuery } from "@datorama/akita-ng-router-store";
import { combineLatest } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import { FirebaseQuery } from "../firebase.query";
import { ProductService } from "./product.service";
import { ProductState, ProductStore } from "./product.store";
import { Product } from "../../model/product";

@Injectable({ providedIn: "root" })
export class ProductQuery extends FirebaseQuery<ProductState> {
  public categoryPath$ = this.routerQuery.selectParams().pipe(
    map((params: Params) => {
      const { category, subcategory, subsubcategory } = params || {};
      return ProductQuery.getPath([category, subcategory, subsubcategory]);
    })
  );

  public roleId$ = this.routerQuery.selectParams().pipe(map((params: Params) => params?.roleId));

  public groupPageId$ = this.routerQuery.selectParams().pipe(map((params: Params) => params?.grouppage));

  public categoryProducts$ = combineLatest([this.categoryPath$, this.roleId$, this.groupPageId$]).pipe(
    switchMap(([path, roleId, groupPageId]) =>
      this.selectAll({
        filterBy: product => {
          if (path) return product.category === path;
          else if (roleId) return product.roles?.includes(roleId);
          else if (groupPageId) return product.groupPages?.includes(groupPageId);
          else return false;
        },
        sortBy: "brand"
      })
    )
  );

  public groupPageProducts$ = this.groupPageId$.pipe(
    switchMap(groupPageId =>
      this.selectAll({
        filterBy: product => product.groupPages?.includes(groupPageId),
        sortBy: "brand"
      })
    )
  );

  public latestProducts$ = this.selectAll({
    limitTo: 6,
    filterBy: product => ProductQuery.isLatestProduct(product, environment.production),
    sortBy: product => product.updated || product.created
  });

  constructor(protected store: ProductStore, protected service: ProductService, private routerQuery: RouterQuery) {
    super(store, service);
  }

  public static getPath(paths: string[]) {
    return paths.filter(_ => _).join("/");
  }

  public static isLatestProduct(product: Product, production: boolean): boolean {
    return (
      product.category !== "portal" &&
      (!production || product.category !== "test") &&
      !product.roles?.length &&
      !product.groupPages?.length
    );
  }
}
